// The Overview's plain parts (tested alone): what needs attention from the engine's answer, the open contracts'
// branch line, a contract's status words. The answer is the engine's /api/overview (engine/api/overview.py).
import { ICONS } from '../../ui/icon-map';

export const fmtN = (v) => Math.round(Number(v) || 0).toLocaleString('en-US');
export const UNPAID_FINES_SCOPE = 'all branches · all time';

// the branches in the company's order: the engine's keys (overview.CODE) and their words
export const BRANCHES = [
    { code: 'T1', short: 'T1', name: 'Terminal 1' }, { code: 'T2', short: 'T2', name: 'Terminal 2' },
    { code: 'BB', short: 'BB', name: 'Business Bay' }, { code: 'INV', short: 'Invygo', name: 'Invygo' },
];
const IN_FILTER = { All: ['T1', 'T2', 'BB', 'INV'], Airport: ['T1', 'T2'], T1: ['T1'], T2: ['T2'], BB: ['BB'], INV: ['INV'],
    'Terminal 1': ['T1'], 'Terminal 2': ['T2'], 'Business Bay': ['BB'], Invygo: ['INV'] };

/** The branches a filter shows (All, Airport, T1 ... or the KPI page's names). */
export const branchesOf = (filter) => BRANCHES.filter((b) => (IN_FILTER[filter] || IN_FILTER.All).includes(b.code));

/** "T1 41 · T2 11 · BB 133 · Invygo 50" from a per-branch figure of the engine (a branch with none is left out). */
export const openByBranchText = (by) => BRANCHES
    .map((b) => (by && by[b.code] ? `${b.short} ${by[b.code]}` : null)).filter(Boolean).join(' · ');

/** A share in whole percent; null without a base. */
export const pct = (n, of) => (of ? Math.round((Number(n) || 0) / of * 100) : null);

/**
 * Every branch side by side (the board's table): contracts open now / opened and returned in the period / past their
 * return date, and
 * the fleet now (in service, rented, ready, in maintenance - the workshop, Business Bay / Invygo (owner, 29 Oct 2026)
 * -, held for bookings - Maintenance status at T1 / T2, owner 1 Oct 2026) - the branches the filter shows, and a
 * Total row.
 */
export function branchRows(data, filter) {
    const c = data?.contracts || {};
    const f = data?.fleet?.byBranch || {};
    const n = (by, code) => (by && by[code]) || 0;
    const rows = branchesOf(filter).map((b) => ({
        code: b.code, branch: b.name,
        open: n(c.openNowByBranch || c.openByBranch, b.code), opened: n(c.openedByBranch, b.code), returned: n(c.returnedByBranch, b.code),
        overdue: n(c.overdueByBranch, b.code),
        fleet: (f[b.code] || {}).inService || 0, rented: (f[b.code] || {}).rented || 0,
        ready: (f[b.code] || {}).ready || 0, maintenance: (f[b.code] || {}).maintenance || 0,
        held: (f[b.code] || {}).held || 0,
    }));
    if (rows.length < 2) return rows;
    const sum = (k) => rows.reduce((a, r) => a + r[k], 0);
    return [...rows, { code: 'TOTAL', branch: 'Total', open: sum('open'), opened: sum('opened'), returned: sum('returned'),
        overdue: sum('overdue'), fleet: sum('fleet'), rented: sum('rented'), ready: sum('ready'), maintenance: sum('maintenance'),
        held: sum('held') }];
}

/** A contract row's status as the tables show it: Open / Overdue Nd / Closed. */
export const contractStatusOf = (r) => {
    if (r.status === 'Overdue') return { label: `Overdue ${r.daysLate}d`, tone: 'danger' };
    if (r.status === 'Open') return { label: 'Open', tone: 'success' };
    return { label: r.status || 'Closed', tone: 'neutral' };
};

/**
 * The period with its hours (owner, 1 Oct 2026: a day is 00:00:00 to 23:59:59 - exactly 24 hours, Dubai): the engine
 * counts [from 00:00, to + 1 day 00:00).
 */
export const periodWindowText = ({ from, to }) => (from === to
    ? `${from} 00:00:00 → 23:59:59`
    : `${from} 00:00:00 → ${to} 23:59:59`);

const plural = (n, word) => `${fmtN(n)} ${word}${n === 1 ? '' : 's'}`;

// the fines page opened on its Fines tab with the filter the figure counts (engine data.stats: unassigned = on no
// contract, needs_review = a review reason, by status Unpaid)
export const FINES_LINK = {
    unpaid: '/traffic-fines?tab=fines&status=Unpaid',
    unmatched: '/traffic-fines?tab=fines&match=unassigned',
    review: '/traffic-fines?tab=fines&review=1',
};

/**
 * What needs attention, from the answer: [{ id, tone, icon, title, sub, action }] - action = { label, to } (a page)
 * or { label, run: 'overdue' | 'sync' } (the caller does it). A part the engine could not read says so instead of
 * looking like "nothing to do".
 */
export function attentionOf(data) {
    if (!data) return [];
    const out = [];
    const fleet = data.fleet;
    if (fleet) {
        fleet.regSoon.filter((x) => x.days <= 7).slice(0, 3).forEach((x) => out.push({
            id: `reg-soon-${x.plate}`, tone: 'danger', icon: ICONS.calendar,
            title: x.days <= 0 ? 'Registration expires today' : `Registration expires in ${plural(x.days, 'day')}`,
            sub: [[x.make, x.model].filter(Boolean).join(' '), x.plate].filter(Boolean).join(' · '),
            action: { label: 'Renew', to: '/registration' },
        }));
        if (fleet.regExpired.length) out.push({
            id: 'reg-expired', tone: 'danger', icon: ICONS.expired,
            title: `${plural(fleet.regExpired.length, 'registration')} already expired`,
            sub: fleet.regExpired.slice(0, 3).map((x) => x.plate).join(' · '),
            action: { label: 'Review', to: '/fleet?expiry=expired' },
        });
    }
    const fines = data.fines;
    if (fines && fines.unmatched.count) out.push({
        id: 'fines-unmatched', tone: 'warning', icon: ICONS.warning,
        title: `${plural(fines.unmatched.count, 'fine')} not matched to a contract`,
        sub: `AED ${fmtN(fines.unmatched.billed)} to charge once matched`,
        // straight to those fines (owner, 3 Oct 2026: "مش عارف اوصل للحجات اللى محتاجه اتنشن")
        action: { label: 'Match', to: FINES_LINK.unmatched },
    });
    const c = data.contracts;
    if (c && c.overdue.length) out.push({
        id: 'overdue', tone: 'danger', icon: ICONS.clock,
        title: `${plural(c.overdue.length, 'contract')} past their return date`,
        sub: `Longest ${c.overdue[0].daysLate} days · ${c.overdue[0].contractNo}`,
        action: { label: 'View', run: 'overdue' },
    });
    if (data.ejar && data.ejar.lastError) out.push({
        id: 'ejar', tone: 'danger', icon: ICONS.refresh,
        title: `Ejar sync failed${data.ejar.lastRun ? ` at ${String(data.ejar.lastRun).slice(11, 16)}` : ''}`,
        sub: String(data.ejar.lastError).slice(0, 80),
        action: { label: 'Retry', run: 'sync' },
    });
    if (fines && fines.review.count) out.push({
        id: 'fines-review', tone: 'warning', icon: ICONS.fines,
        title: `${plural(fines.review.count, 'fine')} need a look`,
        sub: 'Flagged by the engine for a human check',
        action: { label: 'Review', to: FINES_LINK.review },
    });
    Object.entries(data.errors || {}).forEach(([part, why]) => out.push({
        id: `unread-${part}`, tone: 'warning', icon: ICONS.warning,
        title: `${part.charAt(0).toUpperCase()}${part.slice(1)} could not be loaded`, sub: String(why || ''),
    }));
    return out;
}
